import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Divider,
  Stack,
} from '@mui/material';
import {
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  ArrowBack as BackIcon,
  KeyboardDoubleArrowDown as ScrollDownIcon,
  Description as DocumentIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getDocumentApproval,
  getMyDocumentApprovals,
  approveDocument,
  rejectDocument,
  trackDocumentApprovalOpen,
} from '../api/documents';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { useDateLocale } from '../hooks/useDateLocale';
import StatusChip from '../components/StatusChip';
import DocumentViewer from '../components/DocumentViewer';
import SignaturePad, { type SignaturePadRef } from '../components/SignaturePad';
import { useAuth } from '../auth/useAuth';

const DocumentPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();
  const dateLocale = useDateLocale();
  const signatureRef = useRef<SignaturePadRef>(null);
  const signingSectionRef = useRef<HTMLDivElement | null>(null);
  const openTrackedRef = useRef(false);
  const [hasSigned, setHasSigned] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [confirmApproveOpen, setConfirmApproveOpen] = useState(false);
  const { isAuthenticated } = useAuth();

  const { data: document, isLoading, error } = useQuery({
    queryKey: ['document-approval', id],
    queryFn: () => getDocumentApproval(id!),
    enabled: !!id,
  });
  const { data: allDocuments } = useQuery({
    queryKey: ['my-document-approvals'],
    queryFn: getMyDocumentApprovals,
    enabled: isAuthenticated,
  });

  useEffect(() => {
    if (!id || !document || openTrackedRef.current) return;
    openTrackedRef.current = true;

    trackDocumentApprovalOpen(id).catch(() => {
      openTrackedRef.current = false;
    });
  }, [document, id]);

  const scrollToSigningSection = () => {
    signingSectionRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  };

  const approveMutation = useMutation({
    mutationFn: async () => {
      const signatureData = await signatureRef.current?.getSignature();
      return approveDocument(id!, signatureData);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['document-approval', id] });
      queryClient.invalidateQueries({ queryKey: ['my-document-approvals'] });
      enqueueSnackbar(t('document.approvedSuccess'), { variant: 'success' });
      setConfirmApproveOpen(false);
    },
    onError: () => {
      enqueueSnackbar(t('common.error'), { variant: 'error' });
    },
  });

  const rejectMutation = useMutation({
    mutationFn: () => rejectDocument(id!, rejectionReason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['document-approval', id] });
      queryClient.invalidateQueries({ queryKey: ['my-document-approvals'] });
      enqueueSnackbar(t('document.rejectedSuccess'), { variant: 'success' });
      setRejectDialogOpen(false);
    },
    onError: () => {
      enqueueSnackbar(t('common.error'), { variant: 'error' });
    },
  });

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !document) {
    return (
      <Alert severity="error">
        {t('document.notFound')}
      </Alert>
    );
  }

  const isPending = document.status === 'pending';
  const otherPendingDocuments = (allDocuments ?? []).filter(
    (item) => item.id !== document.id && item.status === 'pending',
  );

  return (
    <Box>
      {/* Navigation */}
      <Button
        startIcon={<BackIcon />}
        onClick={() => navigate('/dashboard')}
        sx={{ mb: 2 }}
        size="small"
      >
        {t('common.back')}
      </Button>

      {/* Document header */}
      <Card
        sx={{
          mb: 2,
          border: '1px solid',
          borderColor: 'success.light',
          background: 'linear-gradient(180deg, rgba(237,247,238,0.9) 0%, rgba(255,255,255,1) 65%)',
        }}
      >
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="h5">{document.title}</Typography>
            <StatusChip status={document.status} />
          </Box>

          <Stack spacing={1} sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary">
              {t('document.type', { type: t(`documentType.${document.documentType}`, { defaultValue: document.documentType }) })}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('document.date', { date: format(new Date(document.createdAt), 'd MMMM yyyy', { locale: dateLocale }) })}
            </Typography>
            {document.expiresAt && (
              <Typography variant="body2" color="text.secondary">
                {t('document.expires', { date: format(new Date(document.expiresAt), 'd MMMM yyyy', { locale: dateLocale }) })}
              </Typography>
            )}
          </Stack>

          {document.description && (
            <>
              <Divider sx={{ my: 2 }} />
              <Typography variant="body1">{document.description}</Typography>
            </>
          )}
        </CardContent>
      </Card>

      {document.signingMessage && (
        <Alert
          severity="success"
          sx={{
            mb: 2,
            alignItems: 'flex-start',
            border: '1px solid',
            borderColor: 'success.light',
            '& .MuiAlert-message': { width: '100%' },
          }}
        >
          <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 700 }}>
            Message from Optima
          </Typography>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
            {document.signingMessage}
          </Typography>
        </Alert>
      )}

      {isPending && (
        <Stack spacing={1.5} sx={{ mb: 2 }}>
          <Alert
            severity="info"
            sx={{
              display: { xs: 'flex', md: 'none' },
              alignItems: 'flex-start',
              '& .MuiAlert-message': { width: '100%' },
            }}
          >
            <Typography variant="body2">
              {t('document.scrollInstructionMobile')}
            </Typography>
          </Alert>
          <Button
            variant="contained"
            color="primary"
            endIcon={<ScrollDownIcon />}
            fullWidth
            onClick={scrollToSigningSection}
            sx={{ py: 1.5 }}
          >
            {t('document.scrollToSignature')}
          </Button>
        </Stack>
      )}

      {/* Document content viewer */}
      <DocumentViewer
        htmlContent={document.htmlContent}
        fileUrl={document.fileUrl}
        fileType={document.fileType}
        title={document.title}
      />

      {/* Rejection reason if rejected */}
      {document.status === 'rejected' && document.rejectionReason && (
        <Alert severity="error" sx={{ mb: 2 }}>
          <Typography variant="subtitle2">{t('document.rejectionReasonLabel')}</Typography>
          <Typography variant="body2">{document.rejectionReason}</Typography>
        </Alert>
      )}

      {document.status === 'approved' && otherPendingDocuments.length > 0 && (
        <Box sx={{ mt: 3 }}>
          <Alert severity="success" sx={{ mb: 2 }}>
            {t('document.approvedAndMoreSummary', { count: otherPendingDocuments.length })}
          </Alert>
          <Typography variant="h6" sx={{ mb: 1 }}>
            {t('document.otherPendingTitle')}
          </Typography>
          <Stack spacing={1.5}>
            {otherPendingDocuments.map((item) => (
              <Button
                key={item.id}
                variant="outlined"
                color="primary"
                fullWidth
                startIcon={<DocumentIcon />}
                onClick={() => navigate(`/documents/${item.id}`)}
                sx={{ justifyContent: 'flex-start', py: 1.5 }}
              >
                {item.title}
              </Button>
            ))}
          </Stack>
        </Box>
      )}

      {/* Signature + Actions for pending documents */}
      {isPending && (
        <Box ref={signingSectionRef} sx={{ mt: 2 }}>
          <Alert severity="success" sx={{ mb: 2 }}>
            {t('document.signingStepHint')}
          </Alert>

          {/* Signature pad */}
          <Box sx={{ mb: 3 }}>
            <SignaturePad
              ref={signatureRef}
              onChange={setHasSigned}
              disabled={approveMutation.isPending}
            />
            {!hasSigned && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mt: 0.5, display: 'block', textAlign: 'center' }}
              >
                {t('document.signatureRequired')}
              </Typography>
            )}
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mt: 1, display: 'block', textAlign: 'center' }}
            >
              {t('document.currentIpAddress', { ip: document.currentViewerIpAddress || t('document.ipUnknown') })}
            </Typography>
          </Box>

          {/* Action buttons */}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <Button
              variant="contained"
              color="success"
              size="large"
              startIcon={<ApproveIcon />}
              onClick={() => setConfirmApproveOpen(true)}
              fullWidth
              disabled={!hasSigned || approveMutation.isPending}
              sx={{ py: 1.5 }}
            >
              {t('document.approve')}
            </Button>
            <Button
              variant="outlined"
              color="error"
              size="large"
              startIcon={<RejectIcon />}
              onClick={() => setRejectDialogOpen(true)}
              fullWidth
              disabled={rejectMutation.isPending}
              sx={{ py: 1.5 }}
            >
              {t('document.reject')}
            </Button>
          </Stack>
        </Box>
      )}

      {!isPending && document.signerIpAddress && (
        <Alert severity="info" sx={{ mt: 2 }}>
          {t('document.signedIpAddress', { ip: document.signerIpAddress })}
        </Alert>
      )}

      {/* Confirm approve dialog */}
      <Dialog open={confirmApproveOpen} onClose={() => setConfirmApproveOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>{t('document.confirmApproveTitle')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('document.confirmApproveMessage', { title: document.title })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmApproveOpen(false)}>{t('common.cancel')}</Button>
          <Button
            variant="contained"
            color="success"
            onClick={() => approveMutation.mutate()}
            disabled={approveMutation.isPending}
          >
            {approveMutation.isPending ? <CircularProgress size={20} /> : t('document.approve')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reject dialog */}
      <Dialog open={rejectDialogOpen} onClose={() => setRejectDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{t('document.rejectTitle')}</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            {t('document.rejectMessage', { title: document.title })}
          </Typography>
          <TextField
            label={t('document.rejectionReasonInput')}
            multiline
            rows={3}
            fullWidth
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.target.value)}
            required
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectDialogOpen(false)}>{t('common.cancel')}</Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => rejectMutation.mutate()}
            disabled={rejectMutation.isPending || !rejectionReason.trim()}
          >
            {rejectMutation.isPending ? <CircularProgress size={20} /> : t('document.reject')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DocumentPage;
